import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

export default function useActivityUnreadCount(uid) {
  const [pendingConnectionRequestCount, setPendingConnectionRequestCount] = useState(0);
  const [unreadCommentCount, setUnreadCommentCount] = useState(0);
  const [unreadMentionCount, setUnreadMentionCount] = useState(0);

  useEffect(() => {
    if (!uid) {
      setPendingConnectionRequestCount(0);
      setUnreadCommentCount(0);
      setUnreadMentionCount(0);
      return undefined;
    }

    const requestsRef = collection(db, "users", uid, "connectionRequests");
    const unreadCommentQ = query(
      collection(db, "timeCapsules"),
      where("unreadCommentUserIds", "array-contains", uid)
    );
    const unreadMentionsQ = query(
      collection(db, "users", uid, "activity"),
      where("isRead", "==", false)
    );

    const unsubRequests = onSnapshot(
      requestsRef,
      (snap) => {
        setPendingConnectionRequestCount(snap.size);
      },
      (err) => {
        console.warn("Failed to read activity connection requests:", err);
      }
    );

    const unsubComments = onSnapshot(
      unreadCommentQ,
      (snap) => {
        setUnreadCommentCount(snap.size);
      },
      (err) => {
        console.warn("Failed to read activity unread comments:", err);
      }
    );

    const unsubMentions = onSnapshot(
      unreadMentionsQ,
      (snap) => {
        const count = snap.docs.reduce((acc, docSnap) => {
          const data = docSnap.data() || {};
          return String(data.type || "").trim() === "capsuleMention" ? acc + 1 : acc;
        }, 0);
        setUnreadMentionCount(count);
      },
      (err) => {
        console.warn("Failed to read activity unread mentions:", err);
      }
    );

    return () => {
      unsubRequests();
      unsubComments();
      unsubMentions();
    };
  }, [uid]);

  const unreadCount = useMemo(
    () =>
      Number(pendingConnectionRequestCount || 0) +
      Number(unreadCommentCount || 0) +
      Number(unreadMentionCount || 0),
    [pendingConnectionRequestCount, unreadCommentCount, unreadMentionCount]
  );

  return {
    unreadCount,
    pendingConnectionRequestCount,
    unreadCommentCount,
    unreadMentionCount,
  };
}
